/**
 * @copyright © 2026 ASAP Technology. All rights reserved.
 * @license BSL-1.1 (see LICENSE file)
 *
 * @file stateChannel.ts
 * @description Canal backend→frontend para el estado normalizado del flujo de pago.
 */

const logger = {
  info: (...args: unknown[]) => console.info(...args),
  error: (...args: unknown[]) => console.error(...args),
};

export type PaymentStatus =
  | 'pending'
  | 'processing'
  | 'approved'
  | 'refused'
  | 'cancelled'
  | 'expired'
  | 'error';

export interface PaymentStatusPayload {
  status: PaymentStatus;
  paymentCompleted: boolean;
  flowCompleted: boolean;
  canFinalizeOrder: boolean;
  orderStock?: number;
  remainingPayments?: number;
  nextPayment?: {
    transactionAmount: number;
    currencyCode: string;
    reference: string;
    methodKind: 'card' | 'transfer' | 'wallet' | 'other';
    formToken: string;
    qrBase64?: string;
    transactionValidThru?: string;
  };
  responseActions: string[];
  responseMessage: string;
  retryTime: number;
  updatedAt: string;
}

export function isRecoverableTerminalStatus(status: PaymentStatus): boolean {
  return ['refused', 'cancelled', 'expired', 'error'].includes(status);
}

/** `canFinalizeOrder` nunca es una autoridad independiente del resultado. */
export function isPaymentFlowFinalizable(
  payload: Pick<PaymentStatusPayload, 'status' | 'flowCompleted'>,
): boolean {
  return ['approved', 'refused'].includes(payload.status)
    && payload.flowCompleted;
}

/** Una aprobación parcial sigue viva; un resultado negativo cierra el intento. */
export function isPaymentStatusTerminal(
  payload: Pick<PaymentStatusPayload, 'status' | 'flowCompleted'>,
): boolean {
  return isRecoverableTerminalStatus(payload.status)
    || isPaymentFlowFinalizable(payload);
}

/**
 * Aplica en el navegador la misma invariancia que el backend: sólo un flujo
 * aprobado o rechazado y completo puede habilitar Final.
 */
export function enforceFinalizationInvariant<T extends PaymentStatusPayload>(
  payload: T,
): T {
  const canFinalizeOrder = isPaymentFlowFinalizable(payload);
  if (payload.canFinalizeOrder === canFinalizeOrder) return payload;
  return { ...payload, canFinalizeOrder };
}

export interface StateChannelOptions {
  /** Token efímero del formulario. El backend lo traduce al InitialToken público. */
  formToken: string;
  configuration?: Record<string, unknown>;
  onStateChange: (payload: PaymentStatusPayload) => void;
}

export class StateChannel {
  private ws: WebSocket | null = null;
  private pollingTimer: ReturnType<typeof setTimeout> | null = null;
  private pollingInterval = 2000;
  private readonly maxPollingInterval = 10000;
  private pollingActive = false;
  private isDestroyed = false;
  private trackingCompleted = false;

  constructor(private readonly options: StateChannelOptions) {}

  public start(): void {
    const config = (this.options.configuration as {
      AdditionalConfig?: { EnableRealTimeStatus?: boolean };
    } | undefined)?.AdditionalConfig;

    if (config?.EnableRealTimeStatus === false) {
      logger.info('StateChannel: seguimiento en tiempo real deshabilitado.');
      return;
    }

    this.initWebSocket();
  }

  private initWebSocket(): void {
    if (this.isDestroyed) return;

    try {
      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const formToken = encodeURIComponent(this.options.formToken);
      const wsUrl = `${protocol}//${window.location.host}/api/payment/status/ws?formToken=${formToken}`;
      this.ws = new WebSocket(wsUrl);

      this.ws.onopen = () => {
        logger.info('StateChannel: WebSocket conectado.');
      };

      this.ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data) as PaymentStatusPayload & { type?: string };
          if (data.type === 'heartbeat' || data.type === 'ping') return;
          if (data.type === 'status_update') this.handleStatusUpdate(data);
        } catch {
          // Un heartbeat ofuscado o un payload inválido no cambia el estado.
        }
      };

      this.ws.onerror = (error) => {
        logger.error('StateChannel: WebSocket falló; se usa polling.', error);
        this.fallbackToPolling();
      };

      this.ws.onclose = () => {
        if (!this.isDestroyed && !this.trackingCompleted) this.fallbackToPolling();
      };
    } catch (error) {
      logger.error('StateChannel: no se pudo iniciar WebSocket; se usa polling.', error);
      this.fallbackToPolling();
    }
  }

  private fallbackToPolling(): void {
    if (this.isDestroyed || this.pollingActive) return;
    this.pollingActive = true;

    if (this.ws) {
      this.ws.onclose = null;
      this.ws.close();
      this.ws = null;
    }

    this.poll();
  }

  private async poll(): Promise<void> {
    if (this.isDestroyed) return;

    try {
      const formToken = encodeURIComponent(this.options.formToken);
      const response = await fetch(`/api/payment/status?formToken=${formToken}`);

      if (!response.ok) throw new Error('Polling request failed');

      const data = await response.json() as PaymentStatusPayload;
      if (this.isDestroyed) return;
      this.handleStatusUpdate(data);
      if (Number.isFinite(data.retryTime)) {
        this.pollingInterval = Math.min(
          Math.max(1000, data.retryTime),
          this.maxPollingInterval,
        );
      }
    } catch (error) {
      logger.error('StateChannel: error consultando estado.', error);
      this.pollingInterval = Math.min(
        Math.round(this.pollingInterval * 1.5),
        this.maxPollingInterval,
      );
    }

    if (!this.isDestroyed && !this.trackingCompleted) {
      this.pollingTimer = setTimeout(() => {
        this.pollingTimer = null;
        void this.poll();
      }, this.pollingInterval);
    }
  }

  private handleStatusUpdate(payload: PaymentStatusPayload): void {
    const safePayload = enforceFinalizationInvariant(payload);
    this.trackingCompleted = isPaymentStatusTerminal(safePayload);
    this.options.onStateChange(safePayload);
    if (this.trackingCompleted) this.destroy();
  }

  public destroy(): void {
    this.isDestroyed = true;
    if (this.ws) {
      this.ws.onclose = null;
      this.ws.close();
      this.ws = null;
    }
    if (this.pollingTimer) {
      clearTimeout(this.pollingTimer);
      this.pollingTimer = null;
    }
  }
}
